import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RFQProvider } from "./context/RFQContext";
import { ContentProvider } from "./context/ContentContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import RFQModal from "./components/RFQModal";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import SiteStatusPage from "./components/SiteStatusPage";
import { useContent } from "./context/ContentContext";
import { useAdminAuth } from "./context/AdminAuthContext";
import { Link, useLocation } from "react-router-dom";

import Home from "./pages/Home";
import About from "./pages/About";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Services from "./pages/Services";
import Quality from "./pages/Quality";
import GlobalPresence from "./pages/GlobalPresence";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminArticleEdit from "./pages/admin/AdminArticleEdit";
import AdminContent from "./pages/admin/AdminContent";
import AdminInbox from "./pages/admin/AdminInbox";
import AdminSettings from "./pages/admin/AdminSettings";

function PublicSite({ children }) {
  // Toggle on <html> so portals (RFQ modal, toasts) inherit the dark tokens too.
  useEffect(() => {
    document.documentElement.classList.add("theme-dark");
    return () => document.documentElement.classList.remove("theme-dark");
  }, []);
  const { content, loading } = useContent();
  const { user } = useAdminAuth();
  const { search } = useLocation();
  const previewing = new URLSearchParams(search).get("preview") === "status";
  const status = content.site_status || {};
  const hidden = status.mode === "coming_soon" || status.mode === "maintenance";

  // Never flash the real site before we know whether it is switched off.
  if (loading) return <div className="min-h-screen bg-[#0b1b2b]" />;
  // Visitors see the placeholder page; a signed-in admin still sees the real site.
  if (hidden && (!user || previewing)) return <SiteStatusPage status={status} />;

  return (
    <div className="theme-dark min-h-screen flex flex-col bg-background text-foreground">
      {hidden && user && (
        <div className="fixed bottom-0 inset-x-0 z-[70] bg-[#d9a441] text-[#0c1626] text-sm px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-3 shadow-[0_-8px_30px_rgba(0,0,0,0.35)]">
          <span className="font-medium">
            Site is in {status.mode === "maintenance" ? "Under Maintenance" : "Coming Soon"} mode — visitors see a placeholder page; only you can see this.
          </span>
          <Link to="/admin/settings" className="underline font-semibold">Change in Settings</Link>
        </div>
      )}
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <RFQModal />
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <ContentProvider>
          <AdminAuthProvider>
            <RFQProvider>
              <Routes>
                {/* Admin */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="articles/new" element={<AdminArticleEdit />} />
                  <Route path="articles/:id" element={<AdminArticleEdit />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="rfq" element={<AdminInbox kind="rfq" />} />
                  <Route path="messages" element={<AdminInbox kind="messages" />} />
                  <Route path="inbox" element={<Navigate to="/admin/rfq" replace />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                {/* Public site */}
                <Route path="/" element={<PublicSite><Home /></PublicSite>} />
                <Route path="/about" element={<PublicSite><About /></PublicSite>} />
                <Route path="/products" element={<PublicSite><Products /></PublicSite>} />
                <Route path="/products/:slug" element={<PublicSite><ProductDetail /></PublicSite>} />
                <Route path="/services" element={<PublicSite><Services /></PublicSite>} />
                <Route path="/quality" element={<PublicSite><Quality /></PublicSite>} />
                <Route path="/global-presence" element={<PublicSite><GlobalPresence /></PublicSite>} />
                <Route path="/contact" element={<PublicSite><Contact /></PublicSite>} />
                <Route path="/blog" element={<PublicSite><Blog /></PublicSite>} />
                <Route path="/blog/:slug" element={<PublicSite><BlogPost /></PublicSite>} />
              </Routes>
              <Toaster />
            </RFQProvider>
          </AdminAuthProvider>
        </ContentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
